/**
 * Little page effects around Bailey the desk pet: dust when he lands or
 * sweeps, sparkles, flying emails, a camera flash, hearts, Zzz, music notes.
 * Plain DOM nodes over the page, removed once their animation is over, and
 * nothing at all with reduced motion. Styles are the .bp-fx rules in
 * BaileyPet.css.
 */

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

function spawn(className, x, y, vars = {}, html = '') {
  const n = document.createElement('span');
  n.className = `bp-fx ${className}`;
  n.setAttribute('aria-hidden', 'true');
  n.style.left = `${x}px`;
  n.style.top = `${y}px`;
  Object.keys(vars).forEach((k) => n.style.setProperty(`--${k}`, vars[k]));
  if (html) n.innerHTML = html;
  document.body.appendChild(n);
  setTimeout(() => n.remove(), 2200);
  return n;
}

const rand = (a, b) => a + Math.random() * (b - a);
const ms = (v) => `${Math.round(v)}ms`;
const px = (v) => `${Math.round(v)}px`;

const STAR = '<svg viewBox="0 0 20 20"><path d="M10 0C11 7 13 9 20 10C13 11 11 13 10 20C9 13 7 11 0 10C7 9 9 7 10 0Z" fill="currentColor"/></svg>';
const HEART = '<svg viewBox="0 0 20 18"><path d="M10 17.5C4 13 0 9.8 0 5.6 0 2.5 2.4 0 5.4 0 7.4 0 9 1.1 10 2.7 11 1.1 12.6 0 14.6 0 17.6 0 20 2.5 20 5.6 20 9.8 16 13 10 17.5Z" fill="currentColor"/></svg>';
const MAIL = '<svg viewBox="0 0 26 18"><rect x="1" y="1" width="24" height="16" rx="3" fill="#fffaf1" stroke="#29282d" stroke-width="1.6"/><path d="M2.5 3L13 10.5 23.5 3" fill="none" stroke="#29282d" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/**
 * A puff of dust at a point on the page: `spread` sends it out sideways
 * along the floor (a bump, a broom), otherwise it drifts up and away.
 */
export function dust(x, y, { count = 8, spread = true, size = 1 } = {}) {
  if (reducedMotion()) return;
  for (let i = 0; i < count; i += 1) {
    const dir = spread ? (i % 2 ? 1 : -1) : (Math.random() - 0.5) * 2;
    const reach = spread ? 18 + Math.random() * 34 : 8 + Math.random() * 14;
    spawn('bp-dust', x + (spread ? dir * Math.random() * 10 : dir * 8), y, {
      dx: px(dir * reach),
      dy: px(spread ? -(4 + Math.random() * 14) : -(14 + Math.random() * 18)),
      sz: px((8 + Math.random() * 10) * size),
      delay: ms(Math.random() * 70),
    });
  }
}

const EFFECTS = {
  sparkle(x, y, s) {
    [[-26, -8, 14], [22, -18, 18], [4, 10, 11], [30, 12, 9]].forEach(([dx, dy, size], i) => {
      spawn('bp-sparkle', x + dx * s, y + dy * s, { sz: px(size * s), delay: ms(i * 110) }, STAR);
    });
  },
  dust(x, y, s) {
    dust(x, y, { count: 5, spread: true, size: 0.75 * s });
  },
  mail(x, y, s) {
    const dir = Math.random() > 0.5 ? 1 : -1;
    spawn('bp-mail', x, y, { dx: px(dir * rand(50, 70) * s), dy: px(-rand(70, 95) * s), spin: `${dir * rand(14, 24)}deg`, sz: px(26 * s) }, MAIL);
  },
  flash(x, y, s) {
    spawn('bp-flash', x, y, { sz: px(64 * s) });
    [0, 1, 2, 3].forEach((i) => spawn('bp-flash-ray', x, y, { angle: `${45 + i * 90}deg`, len: px(22 * s) }));
  },
  hearts(x, y, s) {
    [[-12, 0, 13], [8, -6, 16], [22, 4, 11]].forEach(([dx, dy, size], i) => {
      spawn('bp-heart', x + dx * s, y + dy * s, { sz: px(size * s), dx: px(rand(-8, 8) * s), dy: px(-rand(34, 50) * s), delay: ms(i * 160) }, HEART);
    });
  },
  zzz(x, y, s) {
    [0, 1, 2].forEach((i) => {
      spawn('bp-zzz', x + i * 7 * s, y - i * 4 * s, { sz: px((11 + i * 4) * s), dx: px((18 + i * 6) * s), dy: px(-(34 + i * 8) * s), delay: ms(i * 240) }).textContent = 'z';
    });
  },
  alert(x, y, s) {
    spawn('bp-alert', x, y, { sz: px(24 * s) }).textContent = '!';
  },
  note(x, y, s) {
    const n = spawn('bp-note', x, y, { sz: px(rand(15, 20) * s), dx: px(rand(-14, 14) * s), dy: px(-rand(40, 56) * s) });
    n.textContent = Math.random() > 0.5 ? '♪' : '♫';
  },
  sneeze(x, y, s) {
    dust(x, y, { count: 7, spread: false, size: 0.9 * s });
    [0, 1, 2, 3, 4].forEach((i) => {
      const a = (-150 + i * 30) * (Math.PI / 180);
      spawn('bp-droplet', x, y, { dx: px(Math.cos(a) * 34 * s), dy: px(Math.sin(a) * -22 * s + 10 * s), sz: px(5 * s), delay: ms(i * 20) });
    });
  },
  sigh(x, y, s) {
    spawn('bp-sigh', x, y, { sz: px(18 * s), dx: px(26 * s), dy: px(-10 * s) });
  },
};

/** Play one effect at a point on the page. `scale` is 1 at desktop pet size. */
export function playFx(type, x, y, scale = 1) {
  if (reducedMotion() || !EFFECTS[type]) return;
  EFFECTS[type](x, y, scale);
}
